import api from '@/core/api/client';

/**
 * Cross-tenant Sync Gateways API (staff/manager).
 *
 * **TODO(w1-manager-api)**: endpoints `/api/connector/*` ainda não existem
 * no backend. Próxima sub-branch:
 *   feat/w0-manager-sync-endpoints — replica os endpoints staff-side com
 *   auth via staffAuthMiddleware (impersonação para visibilidade cross-tenant).
 *
 * Por agora, o serviço aponta para os paths previstos. UI já fica pronta para
 * conectar quando o backend expor.
 */
export interface ManagerConnectorGateway {
  id: string;
  companyId: string;
  companyName: string;
  name: string;
  integrationId: string;
  adapterId: string;
  adapterVersion: string | null;
  connectorVersion: string | null;
  pendingForceUpdateVersion: string | null;
  /** Quando o staff pediu o force-update pendente (ISO 8601). */
  pendingForceUpdateRequestedAt?: string | null;
  /** Reportado pelo próprio conector no heartbeat. null = build antiga que não informa. */
  autoUpdateEnabled?: boolean | null;
  /** Versão do shell (app/serviço) reportada no heartbeat. */
  shellVersion?: string | null;
  /** P3.1 (issue #62): versão do manifest JSON ativo no sidecar; null em
   *  modo legacy (sidecar sem manifest baixado). */
  manifestVersion: string | null;
  /** P3.3: ISO 8601 quando updates estão pausados; null se pausa ativa. */
  updatesPausedUntil: string | null;
  /** P3.3: motivo informado pelo staff ao pausar. */
  updatesPausedReason: string | null;
  status: string;
  /** Derivado pelo backend considerando staleness do heartbeat (90s).
   *  Use isto em vez de `status` na UI — staleness não vira active estagnado. */
  effectiveStatus: string;
  enabled: boolean;
  releaseChannel: string;
  targetVersion: string | null;
  lastSeenAt: string | null;
  outboxDepth: number | null;
  dlqCount: number;
  createdAt: string;
}

export interface ManagerConnectorOverview {
  totalGateways: number;
  activeGateways: number;
  offlineGateways: number;
  totalDlqPending: number;
  gatewaysByAdapter: Array<{ adapterId: string; count: number; active: number }>;
  /** Top 10 tenants por outbox lag, em ordem decrescente. */
  topLaggingTenants: Array<{ companyId: string; companyName: string; pgLagSec: number | null; outboxDepth: number }>;
}

// ============================================================================
// P1.4 / P1.5 / P1.7: dashboard de release do sidecar.
// ============================================================================

export interface SidecarRelease {
  version: string;
  adapter: string;
  channel: 'stable' | 'dev';
  sha256: string;
  size_bytes: number;
  min_shell_version: string | null;
  release_notes: string | null;
  released_at: string;
  pinned_gateway_count: number;
}

export interface SidecarRollback {
  id: string;
  gateway_id: string;
  from_version: string;
  to_version: string;
  reason: string;
  crashes_in_window: number | null;
  reported_at: string;
}

/**
 * P3.2 (issue #62): linha de "top falhas" — agregada por
 * (handlerName, errorClass) num gateway específico.
 */
export interface GatewayTopFailure {
  handlerName: string | null;
  errorClass: string;
  count: number;
  lastFailedAt: string;
}

/**
 * P3.4 (issue #62): pedido de verbose log mode num gateway.
 */
export type LogRequestStatus =
  | 'requested'
  | 'active'
  | 'expired'
  | 'uploaded'
  | 'cancelled';

export interface GatewayLogRequest {
  id: string;
  level: 'trace' | 'debug' | 'info';
  durationMin: number;
  expiresAt: string;
  status: LogRequestStatus;
  reason: string | null;
  bundleUrl: string | null;
  ackedAt: string | null;
  uploadedAt: string | null;
  createdAt: string;
}

export const managerConnectorService = {
  async overview(): Promise<ManagerConnectorOverview> {
    const { data } = await api.get('/connector/overview');
    return data;
  },

  /** P1.4: lista releases recentes. */
  async listSidecarReleases(opts?: {
    adapter?: string;
    channel?: 'stable' | 'dev';
    limit?: number;
  }): Promise<SidecarRelease[]> {
    const { data } = await api.get('/connector/sidecar/releases', { params: opts });
    return data.releases ?? [];
  },

  /** P1.4: lista rollbacks recentes. */
  async listSidecarRollbacks(limit?: number): Promise<SidecarRollback[]> {
    const { data } = await api.get('/connector/sidecar/rollbacks', { params: { limit } });
    return data.rollbacks ?? [];
  },

  /** P1.4 + P1.7: força update individual num gateway. */
  async forceUpdateGateway(gatewayId: string, version: string, reason?: string): Promise<void> {
    await api.post(`/connector/gateways/${gatewayId}/sidecar/force-update`, { version, reason });
  },

  /** P1.7: cancela pending force-update. */
  async clearForceUpdate(gatewayId: string): Promise<void> {
    await api.delete(`/connector/gateways/${gatewayId}/sidecar/force-update`);
  },

  /** P1.5: pin/unpin gateway numa release canary. */
  async setCanaryPin(version: string, gatewayId: string, pinned: boolean): Promise<{
    pinned_gateway_count: number;
    pinned: boolean;
  }> {
    const { data } = await api.put(`/connector/sidecar/releases/${version}/canary-pin`, {
      gateway_id: gatewayId,
      pinned,
    });
    return data;
  },

  async listGateways(params?: { adapterId?: string; status?: string; companyId?: string }): Promise<ManagerConnectorGateway[]> {
    const { data } = await api.get('/connector/gateways', { params });
    return data.gateways ?? [];
  },

  async setTargetVersion(gatewayId: string, targetVersion: string | null): Promise<void> {
    await api.patch(`/connector/gateways/${gatewayId}/target-version`, { targetVersion });
  },

  async setEnabled(gatewayId: string, enabled: boolean): Promise<void> {
    await api.patch(`/connector/gateways/${gatewayId}/enabled`, { enabled });
  },

  async deleteGateway(gatewayId: string): Promise<void> {
    await api.delete(`/connector/gateways/${gatewayId}`);
  },
  async createRepairPairingCode(gatewayId: string): Promise<{ code: string; expiresAt: string; gatewayId: string }> {
    const { data } = await api.post(`/connector/gateways/${gatewayId}/repair-pairing`);
    return data;
  },

  async bulkRollback(adapterId: string, targetVersion: string): Promise<{ affected: number }> {
    const { data } = await api.post('/connector/bulk-rollback', { adapterId, targetVersion });
    return data;
  },

  /** P3.2: top falhas (agregadas por handler+errorClass) num gateway. */
  async getTopFailures(gatewayId: string, limit = 5): Promise<GatewayTopFailure[]> {
    const { data } = await api.get(`/connector/gateways/${gatewayId}/top-failures`, {
      params: { limit },
    });
    return data.failures ?? [];
  },

  /** P3.3: pausa auto-update até `until` (ISO 8601). */
  async pauseUpdates(
    gatewayId: string,
    payload: { until: string; reason?: string }
  ): Promise<void> {
    await api.post(`/connector/gateways/${gatewayId}/pause-updates`, payload);
  },

  /** P3.3: retoma auto-update (limpa pausa). */
  async resumeUpdates(gatewayId: string): Promise<void> {
    await api.delete(`/connector/gateways/${gatewayId}/pause-updates`);
  },

  /** P3.4: cria pedido de verbose log mode. */
  async createLogRequest(
    gatewayId: string,
    payload: { level: 'trace' | 'debug' | 'info'; durationMin: number; reason?: string }
  ): Promise<GatewayLogRequest> {
    const { data } = await api.post(
      `/connector/gateways/${gatewayId}/log-requests`,
      payload
    );
    return data;
  },

  /** P3.4: lista pedidos recentes (max 10). */
  async listLogRequests(gatewayId: string): Promise<GatewayLogRequest[]> {
    const { data } = await api.get(`/connector/gateways/${gatewayId}/log-requests`);
    return data.requests ?? [];
  },

  /** P3.4: cancela pedido pendente/ativo. */
  async cancelLogRequest(gatewayId: string, requestId: string): Promise<void> {
    await api.delete(`/connector/gateways/${gatewayId}/log-requests/${requestId}`);
  },

  /** Sprint Homolog: lista releases do MSI/Server bundle pra modal de download. */
  async listInstallerReleases(includePrerelease = false, limit = 20): Promise<InstallerRelease[]> {
    const { data } = await api.get('/connector/installer/releases', {
      params: { include_prerelease: includePrerelease, limit },
    });
    return data.releases ?? [];
  },
};

/** `server` é o MSI Server; `server-zip` é o ZIP + PowerShell do Server Core. */
export type InstallerVariant = 'shell' | 'server' | 'server-zip';

/** Sprint Homolog: release do instalador (MSI Desktop, MSI Server e ZIP Server Core). */
export interface InstallerRelease {
  tag: string;
  prerelease: boolean;
  published_at: string;
  assets: Array<{
    name: string;
    size_bytes: number;
    variant: InstallerVariant | 'other';
  }>;
}
